import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { nanosToMillis, updateUnits } from '@/datetime/dateTime.utilities';
import { NUMBER_CONVERSIONS } from '@/main/app.constants';
import { workbenchTextContextEnrichment } from '@/services/systemConfiguration.utilities';
import { computeCapsuleTable } from '@/utilities/formula.utilities';
import { sqTablesApi } from '@seeqdev/api/endpoint/TablesApi';
import { OperatorEnum as CapsulesTableFilterOperatorEnum } from '@seeqdev/api/model/CapsulesTableFilterV1';
import { type ColumnHeaderOutputV1, TypeEnum } from '@seeqdev/api/model/ColumnHeaderOutputV1';
import type { TableColumnFilter } from '@/core/tableUtilities/tables.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import type {
  CapsuleFormulaTable,
  CapsuleFormulaTableRow,
  ComputeCapsuleTableParams,
  PropertyColumn,
  StatColumn,
  TableSortParams,
} from '@/utilities/formula.constants';
import type {
  CapsulesTableFilterV1,
  CapsulesTableInputV1,
  CapsulesTableSortV1,
  CapsulesTableStatV1,
  TableColumnOutputV1,
} from '@seeqdev/api/model/models';

// `TableColumnOutputV1.type` is the legacy formula-table type — `'string' | 'number' | 'date'`,
// which downstream consumers compare against `STRING_UOM` etc. The new endpoint returns the
// generic `ColumnType` enum, so translate to keep the legacy contract.
const COLUMN_TYPE_TO_LEGACY: Record<TypeEnum, string> = {
  [TypeEnum.UUID]: 'string',
  [TypeEnum.TEXT]: 'string',
  [TypeEnum.NUMERIC]: 'number',
  [TypeEnum.BOOLEAN]: 'string',
  [TypeEnum.TIMESTAMPTZ]: 'date',
};

const TIME_PROPERTY_NAMES = new Set<string>([SeeqNames.CapsuleProperties.Start, SeeqNames.CapsuleProperties.End]);

/**
 * Sort `column` values from the capsules pane are frontend keys (e.g. `startTime`, `conditionId`)
 * but the backend's in-memory comparator resolves by raw column name (e.g. `Start`, `Condition ID`).
 * Translate via `propertyColumns` so sort actually applies.
 */
const toSortList = (
  sortParams: TableSortParams | undefined,
  propertyColumns: PropertyColumn[],
  statColumns: StatColumn[],
): CapsulesTableSortV1[] => {
  if (!sortParams) return [];
  const backendNameByKey = Object.fromEntries<string>([
    ...propertyColumns.map(({ key, propertyName }) => [key, propertyName] as const),
    ...statColumns.map(({ key }) => [key, key] as const),
  ]);
  const toEntry = ({ sortBy, sortAsc }: { sortBy: string; sortAsc: boolean }): CapsulesTableSortV1 => ({
    column: backendNameByKey[sortBy] ?? sortBy,
    sortOrder: sortAsc ? 'ASC' : 'DESC',
  });
  return [sortParams, ...(sortParams.orderedAdditionalSortPairs ?? [])].map(toEntry);
};

const toStatInputs = (statColumns: StatColumn[]): CapsulesTableStatV1[] =>
  statColumns.map((stat) => ({
    key: stat.key,
    signalId: stat.signalId,
    stat: stat.stat,
    columnSuffix: stat.columnSuffix,
    ...(stat.backingTableUom !== undefined ? { unitOfMeasure: stat.backingTableUom } : {}),
  }));

const isValueWithUnits = (value: unknown): value is ValueWithUnitsItem =>
  typeof value === 'object' && value !== null && 'value' in value && 'units' in value;

const FILTER_OPERATOR_BY_VALUE: Record<string, CapsulesTableFilterOperatorEnum> = {
  [CapsulesTableFilterOperatorEnum.IsLessThan]: CapsulesTableFilterOperatorEnum.IsLessThan,
  [CapsulesTableFilterOperatorEnum.IsLessThanOrEqualTo]: CapsulesTableFilterOperatorEnum.IsLessThanOrEqualTo,
  [CapsulesTableFilterOperatorEnum.IsGreaterThan]: CapsulesTableFilterOperatorEnum.IsGreaterThan,
  [CapsulesTableFilterOperatorEnum.IsGreaterThanOrEqualTo]: CapsulesTableFilterOperatorEnum.IsGreaterThanOrEqualTo,
  [CapsulesTableFilterOperatorEnum.IsEqualTo]: CapsulesTableFilterOperatorEnum.IsEqualTo,
  [CapsulesTableFilterOperatorEnum.IsNotEqualTo]: CapsulesTableFilterOperatorEnum.IsNotEqualTo,
  [CapsulesTableFilterOperatorEnum.IsBetween]: CapsulesTableFilterOperatorEnum.IsBetween,
  [CapsulesTableFilterOperatorEnum.IsNotBetween]: CapsulesTableFilterOperatorEnum.IsNotBetween,
  [CapsulesTableFilterOperatorEnum.IsMatch]: CapsulesTableFilterOperatorEnum.IsMatch,
  [CapsulesTableFilterOperatorEnum.IsNotMatch]: CapsulesTableFilterOperatorEnum.IsNotMatch,
};

const toFilterOperator = (operator: string): CapsulesTableFilterOperatorEnum => {
  const capsulesTableOperator = FILTER_OPERATOR_BY_VALUE[operator];
  if (!capsulesTableOperator) {
    throw new Error(`Unsupported capsules table filter operator: ${operator}`);
  }
  return capsulesTableOperator;
};

/**
 * Backend filter compares primitives only — no unit system. Unit-bearing operands surface as their
 * numeric value, except Duration, whose raw column values are always in seconds: those get converted
 * from whatever unit the user typed (h, min, day, ...) into seconds so the comparison lines up with
 * what the materialized column actually holds.
 */
const toFilterValue = (
  value: TableColumnFilter['values'][number],
  isDurationColumn = false,
): number | string | undefined => {
  if (!isValueWithUnits(value)) return value;
  return isDurationColumn ? updateUnits(value.value, 's', value.units).value : value.value;
};

const toFilterEntry = (column: string, filter: TableColumnFilter, isDurationColumn = false): CapsulesTableFilterV1 => ({
  column,
  operator: toFilterOperator(filter.operator),
  values: filter.values.map((value) => toFilterValue(value, isDurationColumn)),
  usingSelectedValues: filter.usingSelectedValues ?? false,
});

/**
 * `column` is the materialized table's raw header name: `propertyName` for property columns,
 * `key` for stat columns (the backend renames the formula-generated stat header to that key).
 */
const toFilters = (propertyColumns: PropertyColumn[], statColumns: StatColumn[]): CapsulesTableFilterV1[] => [
  ...propertyColumns.flatMap((column) =>
    column.filter && typeof column.propertyName === 'string'
      ? [toFilterEntry(column.propertyName, column.filter, column.propertyName === SeeqNames.Properties.Duration)]
      : [],
  ),
  ...statColumns.flatMap((column) => (column.filter ? [toFilterEntry(column.key, column.filter)] : [])),
];

type RowCell = string | number | boolean | null | undefined;

type ColumnPlanEntry = { outKey: string; srcIdx: number; isTime: boolean };

/**
 * Mirror the old `computeCapsuleTable` row-shape: each backend column lands in the output row under
 * its caller-specified key AND — when that key differs from the raw header name — under the raw
 * header name too. The raw-header copy lets downstream consumers like `fetchTableAndChartCapsules`
 * read `capsule['Capsule ID']` without knowing the caller's column key convention.
 */
const buildColumnPlan = (
  headers: { name: string }[],
  propertyColumns: PropertyColumn[],
  statColumns: StatColumn[],
): ColumnPlanEntry[] => {
  const headerIndexByName = headers.reduce<Record<string, number>>((acc, header, idx) => {
    acc[header.name] = idx;
    return acc;
  }, {});
  const plan: ColumnPlanEntry[] = [];
  const claimedOutKeys = new Set<string>();
  const addEntry = (outKey: string, srcIdx: number, propertyName: string) => {
    if (claimedOutKeys.has(outKey)) return;
    plan.push({ outKey, srcIdx, isTime: TIME_PROPERTY_NAMES.has(propertyName) });
    claimedOutKeys.add(outKey);
  };
  propertyColumns.forEach(({ key, propertyName }) => {
    const srcIdx = headerIndexByName[propertyName];
    if (srcIdx !== undefined) addEntry(key, srcIdx, propertyName);
  });
  statColumns.forEach(({ key }) => {
    const srcIdx = headerIndexByName[key];
    if (srcIdx !== undefined) addEntry(key, srcIdx, key);
  });
  headers.forEach((header, idx) => addEntry(header.name, idx, header.name));
  return plan;
};

const rowToCapsule = (row: RowCell[], plan: ColumnPlanEntry[]): CapsuleFormulaTableRow => {
  const capsule = {} as CapsuleFormulaTableRow & Record<string, unknown>;
  for (const { outKey, srcIdx, isTime } of plan) {
    const raw = row[srcIdx];
    capsule[outKey] = isTime && typeof raw === 'number' ? nanosToMillis(raw) : raw;
  }
  capsule.isUncertain = !_.isNil(capsule.cursorKey);
  if (capsule.cursorKey === -1) {
    capsule.cursorKey = null;
  }
  return capsule as CapsuleFormulaTableRow;
};

/**
 * `getCapsuleCustomPropertyValuesAndUOMs` in the saga reads legacy `header.units` into
 * `capsule.propertiesUOM`, which `getColumnValueAndUOM` uses as the display suffix. The capsules API keeps primitive
 * type and display UOM separate, so only backend-owned `header.unitOfMeasure` is adapted to legacy `units` here.
 */
const toTableColumnOutput = (header: ColumnHeaderOutputV1): TableColumnOutputV1 => {
  const legacyType = COLUMN_TYPE_TO_LEGACY[header.type] ?? 'string';
  const units = header.unitOfMeasure;
  return {
    name: header.name,
    type: legacyType,
    ...(units != null ? { units } : {}),
  };
};

export const fetchCapsulesPaneTableViaComputeCapsuleTable = async (
  params: ComputeCapsuleTableParams,
): Promise<CapsuleFormulaTable> => computeCapsuleTable(params);

export const fetchCapsulesPaneTableViaCapsulesApi = async (
  params: ComputeCapsuleTableParams,
): Promise<CapsuleFormulaTable> => {
  const propertyColumns = params.columns.propertyColumns;
  const statColumns = params.columns.statColumns;
  const body: CapsulesTableInputV1 = {
    conditionIds: params.itemIds,
    columns: propertyColumns.map((column) => column.propertyName),
    start: params.range.start * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND,
    end: params.range.end * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND,
    limit: params.limit,
    offset: params.offset,
    sort: toSortList(params.sortParams, propertyColumns, statColumns),
    stats: toStatInputs(statColumns),
    filters: toFilters(propertyColumns, statColumns),
  };
  const { data } = await sqTablesApi.queryCapsulesTable(body, { cancellationGroup: params.cancellationGroup });
  const headers = data.headers ?? [];
  const rows = data.rows ?? [];
  const plan = buildColumnPlan(headers, propertyColumns, statColumns);
  const table = rows.map((row) => rowToCapsule(row as RowCell[], plan));

  return {
    data: {
      table,
      headers: headers.map(toTableColumnOutput),
      hasNextPage: data.hasMore ?? false,
      timingInformation: '',
      meterInformation: '',
      warningCount: 0,
      warningLogs: [],
    },
  };
};

export const fetchCapsulesPaneTable = async (params: ComputeCapsuleTableParams): Promise<CapsuleFormulaTable> =>
  workbenchTextContextEnrichment()
    ? fetchCapsulesPaneTableViaCapsulesApi(params)
    : fetchCapsulesPaneTableViaComputeCapsuleTable(params);
